import React, { useState, useEffect, useCallback } from 'react';
import { TokenData } from '../hooks/useBondingCurve';
import { MIGRATION_THRESHOLD } from '../config/contracts';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

interface TradingPanelProps {
  token: TokenData | null;
  account: string | null;
  usdtBalance: string;
  tokenBalance: string;
  loading: boolean;
  onBuy: (usdtAmount: string, slippage: number) => Promise<boolean>;
  onSell: (tokenAmount: string, slippage: number) => Promise<boolean>;
  onMigrate: () => Promise<boolean>;
  estimateBuy: (amount: string) => Promise<string>;
  estimateSell: (amount: string) => Promise<string>;
  onBack: () => void;
}

// 根据当前价格生成联合曲线理论走势（非模拟随机数据）
function generateCurveData(token: TokenData): Array<{pct: string; price: number}> {
  const BASE_PRICE = 0.0001;
  const threshold = parseFloat(MIGRATION_THRESHOLD);
  const currentReserve = parseFloat(token.reserveUSDT);
  const points = 20;
  return Array.from({ length: points }, (_, i) => {
    const reserve = (threshold / (points - 1)) * i;
    // 线性曲线近似：price 随 reserve 增长
    const price = BASE_PRICE + (BASE_PRICE * 9 * reserve) / threshold;
    return {
      pct: `${Math.round((reserve / threshold) * 100)}%`,
      price: parseFloat(price.toFixed(8)),
      current: reserve <= currentReserve ? price : null,
    };
  });
}

const SLIPPAGE_OPTIONS = [0.5, 1, 2, 5];

const TradingPanel: React.FC<TradingPanelProps> = ({
  token,
  account,
  usdtBalance,
  tokenBalance,
  loading,
  onBuy,
  onSell,
  onMigrate,
  estimateBuy,
  estimateSell,
  onBack,
}) => {
  const [tab, setTab] = useState<'buy' | 'sell'>('buy');
  const [amount, setAmount] = useState('');
  const [slippage, setSlippage] = useState(2);
  const [customSlippage, setCustomSlippage] = useState('');
  const [estimatedOut, setEstimatedOut] = useState('0');
  const [chartData, setChartData] = useState<any[]>([]);
  const [txStatus, setTxStatus] = useState<'idle' | 'pending' | 'success' | 'error'>('idle');
  const [txMsg, setTxMsg] = useState('');

  useEffect(() => {
    if (token) setChartData(generateCurveData(token));
  }, [token]);

  const updateEstimate = useCallback(async (val: string) => {
    if (!val || parseFloat(val) <= 0) {
      setEstimatedOut('0');
      return;
    }
    if (tab === 'buy') {
      const est = await estimateBuy(val);
      setEstimatedOut(parseFloat(est).toFixed(4));
    } else {
      const est = await estimateSell(val);
      setEstimatedOut(est);
    }
  }, [tab, estimateBuy, estimateSell]);

  useEffect(() => {
    const t = setTimeout(() => updateEstimate(amount), 400);
    return () => clearTimeout(t);
  }, [amount, updateEstimate]);

  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0) return;
    setTxStatus('pending');
    setTxMsg('');
    try {
      let ok: boolean;
      if (tab === 'buy') {
        ok = await onBuy(amount, slippage);
      } else {
        ok = await onSell(amount, slippage);
      }
      if (ok) {
        setTxStatus('success');
        setTxMsg(`${tab === 'buy' ? '购买' : '出售'}成功！`);
        setAmount('');
        setEstimatedOut('0');
      } else {
        setTxStatus('error');
        setTxMsg('交易失败，请重试');
      }
    } catch {
      setTxStatus('error');
      setTxMsg('交易异常');
    }
    setTimeout(() => setTxStatus('idle'), 4000);
  };

  const handleMigrate = async () => {
    setTxStatus('pending');
    const ok = await onMigrate();
    if (ok) {
      setTxStatus('success');
      setTxMsg('迁移成功！流动性已添加到 PancakeSwap');
    } else {
      setTxStatus('error');
      setTxMsg('迁移失败');
    }
    setTimeout(() => setTxStatus('idle'), 5000);
  };

  if (!token) {
    return (
      <div style={styles.empty}>
        <div style={styles.emptyIcon}>📊</div>
        <div style={styles.emptyText}>请从市场选择代币进行交易</div>
        <button style={styles.backBtn} onClick={onBack}>← 返回市场</button>
      </div>
    );
  }

  const progress = token.progress;
  const canMigrate = parseFloat(token.reserveUSDT) >= parseInt(MIGRATION_THRESHOLD);

  const effectiveSlippage = customSlippage ? parseFloat(customSlippage) : slippage;

  return (
    <div style={styles.wrapper}>
      {/* Left: Chart + Info */}
      <div style={styles.left}>
        <button style={styles.backBtn} onClick={onBack}>← 返回市场</button>

        {/* Token Header */}
        <div style={styles.tokenHeader}>
          <div style={styles.tokenIconLg}>
            {token.symbol.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <h2 style={styles.tokenNameLg}>{token.name}</h2>
            <div style={styles.tokenSymbolLg}>${token.symbol}</div>
          </div>
          <div style={styles.priceBig}>
            <div style={styles.priceBigLabel}>当前价格</div>
            <div style={styles.priceBigValue}>${parseFloat(token.currentPrice).toFixed(8)}</div>
          </div>
          {token.migrated && (
            <div style={styles.migratedTag}>✅ 已迁移到 PancakeSwap</div>
          )}
        </div>

        {/* Stats Row */}
        <div style={styles.statsRow}>
          {[
            { label: 'USDT 储备', value: `$${parseFloat(token.reserveUSDT).toLocaleString()}` },
            { label: '代币余额', value: parseFloat(token.tokenBalance).toLocaleString(undefined, { maximumFractionDigits: 2 }) },
            { label: '总供应量', value: parseFloat(token.totalSupply).toLocaleString(undefined, { maximumFractionDigits: 2 }) },
            { label: '迁移进度', value: `${progress.toFixed(1)}%` },
          ].map((s, i) => (
            <div key={i} style={styles.statBox}>
              <div style={styles.statLabel}>{s.label}</div>
              <div style={styles.statValue}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Description & Website */}
        {(token.description || token.website) && (
          <div style={styles.infoBox}>
            {token.description && (
              <div style={styles.infoDesc}>{token.description}</div>
            )}
            {token.website && (
              <a
                href={token.website.startsWith('http') ? token.website : `https://${token.website}`}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.infoWebsite}
                onClick={e => e.stopPropagation()}
              >
                🌐 {token.website}
              </a>
            )}
          </div>
        )}

        {/* Price Chart */}
        <div style={styles.chartBox}>
          <div style={styles.chartTitle}>联合曲线价格走势</div>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00d4ff" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#00d4ff" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="pct" tick={{ fill: '#3d5070', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#3d5070', fontSize: 10 }} axisLine={false} tickLine={false} width={70} tickFormatter={v => `$${v.toFixed(6)}`} />
              <Tooltip
                contentStyle={{ background: '#0f1629', border: '1px solid #1e2d4a', borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: '#6b7d99' }}
                itemStyle={{ color: '#00d4ff' }}
                formatter={(v: any) => [`$${parseFloat(v).toFixed(8)}`, '曲线价格']}
              />
              <Area type="monotone" dataKey="price" stroke="#00d4ff" strokeWidth={2} fill="url(#priceGrad)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Migration Progress */}
        {!token.migrated && (
          <div style={styles.migrationBox}>
            <div style={styles.migrationHeader}>
              <div>
                <div style={styles.migrationTitle}>🥞 自动迁移到 PancakeSwap</div>
                <div style={styles.migrationSub}>
                  当 USDT 储备达到 ${parseInt(MIGRATION_THRESHOLD).toLocaleString()} 时自动将流动性迁移
                </div>
              </div>
              {canMigrate && (
                <button
                  style={styles.migrateBtn}
                  onClick={handleMigrate}
                  disabled={loading}
                >
                  立即迁移
                </button>
              )}
            </div>
            <div style={styles.migBar}>
              <div style={{
                ...styles.migFill,
                width: `${progress}%`,
                background: progress >= 80 ? 'linear-gradient(90deg, #ffd600, #ff6d00)' :
                             'linear-gradient(90deg, #00d4ff, #7c4dff)',
              }} />
            </div>
            <div style={styles.migStats}>
              <span style={{ color: '#00d4ff' }}>${parseFloat(token.reserveUSDT).toLocaleString()} USDT</span>
              <span style={{ color: '#3d5070' }}>目标: ${parseInt(MIGRATION_THRESHOLD).toLocaleString()} USDT</span>
            </div>
          </div>
        )}
      </div>

      {/* Right: Trade Box */}
      <div style={styles.right}>
        <div style={styles.tradeBox}>
          {/* Tabs */}
          <div style={styles.tabs}>
            <button
              style={{ ...styles.tab, ...(tab === 'buy' ? styles.tabActiveBuy : {}) }}
              onClick={() => { setTab('buy'); setAmount(''); setEstimatedOut('0'); }}
            >
              买入
            </button>
            <button
              style={{ ...styles.tab, ...(tab === 'sell' ? styles.tabActiveSell : {}) }}
              onClick={() => { setTab('sell'); setAmount(''); setEstimatedOut('0'); }}
            >
              卖出
            </button>
          </div>

          {/* Balance */}
          <div style={styles.balRow}>
            <span style={styles.balLabel}>
              {tab === 'buy' ? 'USDT 余额' : `${token.symbol} 余额`}
            </span>
            <span style={styles.balValue}>
              {tab === 'buy' ? `${usdtBalance} USDT` : `${tokenBalance} ${token.symbol}`}
            </span>
          </div>

          {/* Amount Input */}
          <div style={styles.inputGroup}>
            <label style={styles.inputLabel}>
              {tab === 'buy' ? '支付 USDT' : `卖出 ${token.symbol}`}
            </label>
            <div style={styles.inputWrap}>
              <input
                type="number"
                style={styles.input}
                placeholder="0.00"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                min="0"
                disabled={token.migrated || loading}
              />
              <span style={styles.inputUnit}>
                {tab === 'buy' ? 'USDT' : token.symbol}
              </span>
            </div>
            {/* Quick Amount Buttons */}
            <div style={styles.quickBtns}>
              {(tab === 'buy'
                ? ['10', '50', '100', '500']
                : ['25%', '50%', '75%', '100%']
              ).map(v => (
                <button
                  key={v}
                  style={styles.quickBtn}
                  onClick={() => {
                    if (tab === 'buy') {
                      setAmount(v);
                    } else {
                      const pct = parseInt(v) / 100;
                      setAmount((parseFloat(tokenBalance) * pct).toFixed(4));
                    }
                  }}
                  disabled={token.migrated || loading}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          {/* Estimated Output */}
          <div style={styles.estimateBox}>
            <div style={styles.estimateRow}>
              <span style={styles.estimateLabel}>
                预估获得
              </span>
              <span style={styles.estimateValue}>
                {estimatedOut} {tab === 'buy' ? token.symbol : 'USDT'}
              </span>
            </div>
            <div style={styles.estimateRow}>
              <span style={styles.estimateLabel}>手续费</span>
              <span style={styles.estimateFee}>1.5%</span>
            </div>
          </div>

          {/* Slippage */}
          <div style={styles.slippageSection}>
            <div style={styles.slippageLabel}>滑点容忍</div>
            <div style={styles.slippageBtns}>
              {SLIPPAGE_OPTIONS.map(s => (
                <button
                  key={s}
                  style={{
                    ...styles.slippageBtn,
                    ...(effectiveSlippage === s && !customSlippage ? styles.slippageBtnActive : {}),
                  }}
                  onClick={() => { setSlippage(s); setCustomSlippage(''); }}
                >
                  {s}%
                </button>
              ))}
              <input
                type="number"
                style={styles.slippageInput}
                placeholder="自定义"
                value={customSlippage}
                onChange={e => setCustomSlippage(e.target.value)}
                min="0.1"
                max="50"
              />
            </div>
          </div>

          {/* Status */}
          {txStatus !== 'idle' && (
            <div style={{
              ...styles.txStatus,
              background: txStatus === 'success' ? 'rgba(0,230,118,0.1)' :
                          txStatus === 'error' ? 'rgba(255,77,109,0.1)' :
                          'rgba(0,212,255,0.1)',
              border: `1px solid ${txStatus === 'success' ? 'rgba(0,230,118,0.3)' :
                         txStatus === 'error' ? 'rgba(255,77,109,0.3)' :
                         'rgba(0,212,255,0.3)'}`,
              color: txStatus === 'success' ? '#00e676' :
                     txStatus === 'error' ? '#ff4d6d' : '#00d4ff',
            }}>
              {txStatus === 'pending' && <span style={styles.txSpinner}>⟳</span>}
              {txStatus === 'success' && '✓ '}
              {txStatus === 'error' && '✗ '}
              {txStatus === 'pending' ? '交易处理中...' : txMsg}
            </div>
          )}

          {/* Submit Button */}
          {!account ? (
            <div style={styles.noWallet}>请先连接钱包</div>
          ) : token.migrated ? (
            <a
              href={`https://pancakeswap.finance/swap?outputCurrency=${token.tokenAddress}`}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.pancakeBtn}
            >
              🥞 前往 PancakeSwap 交易
            </a>
          ) : (
            <button
              style={{
                ...styles.submitBtn,
                background: tab === 'buy'
                  ? 'linear-gradient(135deg, #00e676, #00bcd4)'
                  : 'linear-gradient(135deg, #ff4d6d, #ff6d00)',
                opacity: loading || !amount || parseFloat(amount) <= 0 ? 0.5 : 1,
              }}
              onClick={handleSubmit}
              disabled={loading || !amount || parseFloat(amount) <= 0}
            >
              {loading ? '处理中...' : tab === 'buy' ? `买入 ${token.symbol}` : `卖出 ${token.symbol}`}
            </button>
          )}

          {/* Contract Address */}
          <div style={styles.contractInfo}>
            <span style={styles.contractLabel}>合约地址</span>
            <span style={styles.contractAddr}>
              {token.tokenAddress.slice(0, 10)}...{token.tokenAddress.slice(-8)}
            </span>
            <a
              href={`https://bscscan.com/token/${token.tokenAddress}`}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.bscscanLink}
            >
              BSCScan ↗
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 16,
  },
  left: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  right: {},
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 0',
    gap: 16,
  },
  emptyIcon: {
    fontSize: 48,
  },
  emptyText: {
    color: '#6b7d99',
    fontSize: 16,
  },
  backBtn: {
    background: 'transparent',
    border: '1px solid #1e2d4a',
    color: '#6b7d99',
    fontSize: 13,
    padding: '8px 16px',
    borderRadius: 8,
    cursor: 'pointer',
    alignSelf: 'flex-start',
    transition: 'all 0.2s',
  },
  tokenHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '20px',
    background: '#131929',
    border: '1px solid #1e2d4a',
    borderRadius: 16,
    flexWrap: 'wrap' as const,
  },
  tokenIconLg: {
    width: 56,
    height: 56,
    borderRadius: 14,
    background: 'linear-gradient(135deg, #00d4ff, #7c4dff)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 20,
    fontWeight: 800,
    color: '#fff',
    flexShrink: 0,
  },
  tokenNameLg: {
    fontSize: 22,
    fontWeight: 800,
    color: '#e8eef7',
    margin: 0,
  },
  tokenSymbolLg: {
    fontSize: 14,
    color: '#6b7d99',
    marginTop: 2,
  },
  priceBig: {
    marginLeft: 'auto',
    textAlign: 'right' as const,
  },
  priceBigLabel: {
    fontSize: 11,
    color: '#6b7d99',
    marginBottom: 4,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  priceBigValue: {
    fontSize: 24,
    fontWeight: 800,
    color: '#00d4ff',
  },
  migratedTag: {
    fontSize: 13,
    color: '#00e676',
    background: 'rgba(0,230,118,0.1)',
    border: '1px solid rgba(0,230,118,0.2)',
    padding: '6px 14px',
    borderRadius: 8,
    fontWeight: 600,
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 8,
  },
  infoBox: {
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid #1e2d4a',
    borderRadius: 12,
    padding: '14px 16px',
    marginTop: 8,
  },
  infoDesc: {
    fontSize: 13,
    color: '#a0b0c8',
    lineHeight: 1.6,
    marginBottom: 8,
    whiteSpace: 'pre-wrap' as const,
  },
  infoWebsite: {
    fontSize: 13,
    color: '#00d4ff',
    textDecoration: 'none',
    display: 'inline-block',
  },
  statBox: {
    background: '#131929',
    border: '1px solid #1e2d4a',
    borderRadius: 12,
    padding: '14px 16px',
  },
  statLabel: {
    fontSize: 11,
    color: '#6b7d99',
    marginBottom: 6,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  statValue: {
    fontSize: 16,
    fontWeight: 700,
    color: '#e8eef7',
  },
  chartBox: {
    background: '#131929',
    border: '1px solid #1e2d4a',
    borderRadius: 16,
    padding: '20px',
  },
  chartTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: '#6b7d99',
    marginBottom: 16,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  migrationBox: {
    background: 'linear-gradient(135deg, rgba(0,212,255,0.05), rgba(124,77,255,0.05))',
    border: '1px solid rgba(0,212,255,0.2)',
    borderRadius: 16,
    padding: '20px',
  },
  migrationHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    gap: 12,
  },
  migrationTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: '#e8eef7',
    marginBottom: 4,
  },
  migrationSub: {
    fontSize: 12,
    color: '#6b7d99',
  },
  migrateBtn: {
    background: 'linear-gradient(135deg, #ffd600, #ff6d00)',
    border: 'none',
    color: '#000',
    fontWeight: 700,
    fontSize: 13,
    padding: '10px 20px',
    borderRadius: 10,
    cursor: 'pointer',
    flexShrink: 0,
  },
  migBar: {
    height: 10,
    background: 'rgba(255,255,255,0.06)',
    borderRadius: 5,
    overflow: 'hidden',
    marginBottom: 10,
  },
  migFill: {
    height: '100%',
    borderRadius: 5,
    transition: 'width 0.5s',
  },
  migStats: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 13,
    fontWeight: 600,
  },
  tradeBox: {
    background: '#131929',
    border: '1px solid #1e2d4a',
    borderRadius: 20,
    padding: '20px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 14,
  },
  tabs: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    background: '#0d1220',
    borderRadius: 12,
    padding: 4,
    gap: 4,
  },
  tab: {
    background: 'transparent',
    border: 'none',
    color: '#6b7d99',
    fontSize: 15,
    fontWeight: 600,
    padding: '12px',
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  tabActiveBuy: {
    background: 'rgba(0,230,118,0.15)',
    color: '#00e676',
  },
  tabActiveSell: {
    background: 'rgba(255,77,109,0.15)',
    color: '#ff4d6d',
  },
  balRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  balLabel: {
    fontSize: 12,
    color: '#6b7d99',
  },
  balValue: {
    fontSize: 13,
    fontWeight: 600,
    color: '#e8eef7',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
  },
  inputLabel: {
    fontSize: 12,
    color: '#6b7d99',
    fontWeight: 500,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  inputWrap: {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
  },
  input: {
    width: '100%',
    background: '#0d1220',
    border: '1px solid #1e2d4a',
    borderRadius: 12,
    color: '#e8eef7',
    fontSize: 20,
    fontWeight: 600,
    padding: '14px 80px 14px 16px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  inputUnit: {
    position: 'absolute' as const,
    right: 14,
    fontSize: 13,
    fontWeight: 600,
    color: '#6b7d99',
  },
  quickBtns: {
    display: 'flex',
    gap: 6,
  },
  quickBtn: {
    flex: 1,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid #1e2d4a',
    color: '#6b7d99',
    fontSize: 12,
    fontWeight: 600,
    padding: '6px 4px',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  estimateBox: {
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.05)',
    borderRadius: 12,
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
  },
  estimateRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  estimateLabel: {
    fontSize: 12,
    color: '#6b7d99',
  },
  estimateValue: {
    fontSize: 16,
    fontWeight: 700,
    color: '#00d4ff',
  },
  estimateFee: {
    fontSize: 13,
    color: '#6b7d99',
  },
  slippageSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
  },
  slippageLabel: {
    fontSize: 12,
    color: '#6b7d99',
    fontWeight: 500,
  },
  slippageBtns: {
    display: 'flex',
    gap: 6,
  },
  slippageBtn: {
    flex: 1,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid #1e2d4a',
    color: '#6b7d99',
    fontSize: 12,
    fontWeight: 600,
    padding: '7px 4px',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  slippageBtnActive: {
    background: 'rgba(0,212,255,0.15)',
    border: '1px solid rgba(0,212,255,0.4)',
    color: '#00d4ff',
  },
  slippageInput: {
    width: 70,
    background: '#0d1220',
    border: '1px solid #1e2d4a',
    color: '#e8eef7',
    fontSize: 12,
    padding: '7px 8px',
    borderRadius: 8,
    outline: 'none',
    textAlign: 'center' as const,
  },
  txStatus: {
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: 13,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  txSpinner: {
    display: 'inline-block',
    animation: 'spin 1s linear infinite',
  },
  noWallet: {
    textAlign: 'center' as const,
    color: '#6b7d99',
    fontSize: 14,
    padding: '16px',
    background: 'rgba(255,255,255,0.03)',
    borderRadius: 12,
    border: '1px solid #1e2d4a',
  },
  submitBtn: {
    width: '100%',
    border: 'none',
    color: '#fff',
    fontSize: 16,
    fontWeight: 700,
    padding: '16px',
    borderRadius: 14,
    cursor: 'pointer',
    transition: 'opacity 0.2s',
    letterSpacing: '0.3px',
  },
  pancakeBtn: {
    display: 'block',
    textAlign: 'center' as const,
    background: 'linear-gradient(135deg, #ffd600, #ff6d00)',
    border: 'none',
    color: '#000',
    fontSize: 15,
    fontWeight: 700,
    padding: '16px',
    borderRadius: 14,
    textDecoration: 'none',
  },
  contractInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 14px',
    background: 'rgba(255,255,255,0.02)',
    borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.04)',
  },
  contractLabel: {
    fontSize: 11,
    color: '#3d5070',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    flexShrink: 0,
  },
  contractAddr: {
    fontSize: 11,
    color: '#6b7d99',
    fontFamily: 'monospace',
    flex: 1,
  },
  bscscanLink: {
    fontSize: 11,
    color: '#00d4ff',
    textDecoration: 'none',
    fontWeight: 600,
    flexShrink: 0,
  },
};

export default TradingPanel;
